import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSubmitSuccessEvent } from '../../contexts/events-context';
import {
  ForcedState,
  useForcedState,
} from '../../contexts/forced-state-context';
import {
  FORM_STATUS,
  useFormStatus,
} from '../../contexts/form-status-provider';
import { useTranslation } from '../../contexts/translation-context';
import { type WixFile } from '../form/types';

import { type FileFormat, FileFormatMap } from './file-format';
import { type FileUploadProps } from './file-upload-props';
import { useUploadFile } from './use-upload-file';

export type FileUploadFileState = 'idle' | 'uploading' | 'uploaded' | 'error';
export interface FileUploadFile {
  file: File;
  state: FileUploadFileState;
  errorMessage?: string;
}

type UseFileUploadControllerParams = {
  valueSliderLimit: number;
  value: WixFile[] | undefined | null;
  explanationText: string | undefined;
  target: string;
  onChange: (value: WixFile[] | undefined | null) => void;
  uploadFileFormats: FileFormat[];
  onBlur: () => void;
  onFilesCleared: () => void;
};

export function useFileUploadController({
  valueSliderLimit,
  value,
  explanationText,
  target,
  onChange,
  uploadFileFormats,
  onBlur,
  onFilesCleared,
}: UseFileUploadControllerParams) {
  const [files, setFiles] = useState<FileUploadFile[]>(
    convertValuesToFiles(value),
  );
  const [uploadedFiles, setUploadedFiles] = useState<WixFile[]>(value || []);
  const [_error, setError] = useState<string>();
  const uploadFiles = useUploadFile(target);
  const { t } = useTranslation();
  const forcedState = useForcedState();
  const hover = forcedState.includes(ForcedState.BUTTONS_HOVER);
  const { setFormStatus } = useFormStatus();
  const prevIsUploading = useRef<boolean>(false);

  const currentIsUploading = useMemo(() => {
    const isUploading = files.some((file) => file.state === 'uploading');
    return isUploading;
  }, [files]);

  useEffect(() => {
    if (prevIsUploading.current !== currentIsUploading) {
      prevIsUploading.current = currentIsUploading;
      setFormStatus(
        target,
        currentIsUploading ? FORM_STATUS.BUSY : FORM_STATUS.READY,
      );
    }
  }, [currentIsUploading, setFormStatus, target]);

  const getExplanationText = useCallback(() => {
    if (!files.length) {
      return explanationText;
    }

    const uploadingCount = files.filter((f) => f.state === 'uploading').length;
    if (uploadingCount) {
      const uploadedCount = files.filter(
        (f) => f.state === 'uploaded' && f.file.size > 0,
      ).length;

      const progress = uploadedCount / (uploadedCount + uploadingCount);

      return t(`form.file-upload.uploading`, {
        count: Math.round(progress * 100),
      });
    }

    return (
      explanationText ||
      t(`form.file-upload.explanation-text`, {
        count: files.length,
      })
    );
  }, [files, explanationText, t]);

  const _uploadFiles = useCallback(
    async (filesToUpload: FileUploadFile[]) => {
      await uploadFiles(
        filesToUpload,
        ({ file, uploadedFile }) => {
          setFiles((previousFiles) =>
            previousFiles.map((f) =>
              f.file === file ? { file, state: 'uploaded' } : f,
            ),
          );
          setUploadedFiles((prevFiles) => {
            const newFiles = [...prevFiles].concat(uploadedFile);
            onChange?.(newFiles);
            return newFiles;
          });
        },
        (_err, file) => {
          setFiles((previousFiles) =>
            previousFiles.map((f) =>
              f.file === file
                ? {
                    file,
                    state: 'error',
                    errorMessage: t('form.file-upload.error.upload-failed'),
                  }
                : f,
            ),
          );
        },
      );
    },
    [uploadFiles, onChange, t],
  );

  const handleChange = async (_files: FileList | null) => {
    _error && setError(undefined);
    if (_files) {
      const limitExceeded = files.length + _files.length > valueSliderLimit;

      if (limitExceeded) {
        setError(
          t('form.file-upload.error.upload-limit', { limit: valueSliderLimit }),
        );
        return;
      }

      const filesArr = Array.from(_files);
      const filesToAdd = filesArr.reduce<FileUploadFile[]>((result, _file) => {
        const alreadyAdded = files.some(
          (file) => file.file.name === _file.name,
        );
        if (!alreadyAdded) {
          result.push({
            file: _file,
            state: 'uploading',
          });
        }
        return result;
      }, []);

      setFiles([...files, ...filesToAdd]);
      await _uploadFiles(filesToAdd);
    }
  };

  const handleDelete = (index: number) => {
    const fileToDelete = files[index];
    if (uploadedFiles && onChange && fileToDelete.state !== 'error') {
      const filteredValues = uploadedFiles.filter(
        (f) => f.displayName !== fileToDelete.file.name,
      );
      onChange(filteredValues);
      setUploadedFiles(filteredValues);
      const filteredFiles = files.filter(
        (f) => f.file.name !== fileToDelete.file.name,
      );
      setFiles(filteredFiles);
      filteredFiles.length === 0 && onFilesCleared();
    } else {
      const filteredFiles = files.filter((f) => f !== fileToDelete);
      setFiles(filteredFiles);
      filteredFiles.length === 0 && onFilesCleared();
    }
  };

  const handleBlur = () => {
    _error && setError(undefined);
    onBlur();
  };

  const accept = uploadFileFormats
    .map((format) => FileFormatMap[format])
    .join();

  const onSubmitSuccess = useCallback(() => {
    setFiles([]);
    setUploadedFiles([]);
  }, []);
  useSubmitSuccessEvent(onSubmitSuccess);

  return {
    files,
    _error,
    hover,
    getExplanationText,
    handleChange,
    handleDelete,
    accept,
    handleBlur,
  };
}

function convertValuesToFiles(
  _value: FileUploadProps['value'] = [],
): FileUploadFile[] {
  const value = _value ? _value : [];
  try {
    return value.map((v) => {
      return {
        file: new File([new Int8Array(0)], v.displayName, {
          type: v.fileType,
        }),
        state: 'uploaded',
      };
    });
  } catch (e) {
    console.error(e);
  }
  return [];
}
